import React, { useCallback, useEffect } from 'react';
import { omit } from 'ramda';
import PropTypes from 'prop-types';

import {
  ASC,
  DESIGNATION,
  ID,
  LIMIT,
  SEARCH,
  SORT_BY,
  SORT_DIR,
  TAGS,
  useAlerts,
  useMedicalHistoryActions,
} from 'store/medicalHistory';
import { useAsyncState } from 'utils/useAsyncState';
import Spinner from 'components/FullScreenSpinner';
import PaginationDropDown from 'components/PaginationDropDown';
import Table from 'components/Table';

import { headerFields } from './utils';
import { usePatientMedications } from './hooks';
import Row from './Row';
import DrugButton from './DrugButton';
import { QuestionContainer, Container, Label, SpinnerWrap } from './styles';

export default function MedicationsForm({ readMode }) {
  const { list, drugComparator, patientDrugList, addMedication, removeMedication, editLoading } = usePatientMedications();
  const { fetchDrugs } = useMedicalHistoryActions();
  const [favoriteList, setFavoriteList] = useAsyncState([]);
  const { action, loading, success } = useAlerts(fetchDrugs);
  const getDrugs = useCallback(
    ($) =>
      fetchDrugs({
        ...omit([SEARCH], $),
        ...(($[SEARCH] || '').trim()
          ? { [SEARCH]: $[SEARCH] }
          : {
              [SORT_BY]: DESIGNATION,
              [SORT_DIR]: ASC,
            }),
      }),
    [fetchDrugs]
  );
  const refetchTags = useCallback(() => {
    if (!readMode) action({ [TAGS]: 'favorite', [LIMIT]: 50 });
  }, [action, readMode]);
  const onSelect = useCallback(
    (drug) => {
      if (!drugComparator[drug[ID]]) addMedication(drug);
    },
    [addMedication, drugComparator]
  );
  const renderRow = useCallback(
    (data) => <Row {...data} onRemove={removeMedication} refetchTags={refetchTags} readMode={readMode} />,
    [readMode, removeMedication, refetchTags]
  );

  useEffect(() => {
    if (success?.data) setFavoriteList(success.data);
  }, [success?.data, setFavoriteList]);
  useEffect(() => refetchTags(), [refetchTags]);

  if (readMode)
    return (
      <QuestionContainer label="Augen-Medikamente">
        <Table head={headerFields} list={list} renderRow={renderRow} renderKey={ID} />
      </QuestionContainer>
    );

  return (
    <QuestionContainer label="Augen-Medikamente">
      {Boolean(favoriteList.length) && (
        <>
          <Label>Hinzufügen</Label>
          <Container>
            {favoriteList.map((item) => (
              <DrugButton key={item[ID]} data={item} onSelect={onSelect} isActive={Boolean(drugComparator[item[ID]])} />
            ))}
            {loading && (
              <SpinnerWrap>
                <Spinner height="60px" />
              </SpinnerWrap>
            )}
          </Container>
        </>
      )}
      <PaginationDropDown
        idKey={ID}
        nameKey={DESIGNATION}
        action={getDrugs}
        placeholder="Suchen"
        value={patientDrugList}
        onChange={onSelect}
        disableIfSelected
        withoutSelectedList
        withoutRemoving
        onlyOne
      />
      <Label>Aktuelle Medikamente</Label>
      <Table head={headerFields} list={list} renderRow={renderRow} renderKey={ID} loading={editLoading} />
    </QuestionContainer>
  );
}

MedicationsForm.propTypes = { readMode: PropTypes.bool.isRequired };
